import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Paper } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Text,
  RichText,
  LinkField,
  TextField,
  RichTextField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';

// Token storage key (same as AuthContext)
const TOKEN_STORAGE_KEY = 'lma-auth-token';
const USERNAME_STORAGE_KEY = 'lma-username';
const CDP_PROFILE_DATA_STORAGE_KEY = 'cdp-profile-data';

// Sitecore field types
interface LogoutFields {
  Title?: TextField;
  Summary?: TextField;
  Description?: RichTextField;
  InfoText?: TextField;
  CTAButton?: LinkField;
}

type LogoutProps = ComponentProps & {
  fields: LogoutFields;
};

const Logout = (props: LogoutProps): JSX.Element => {
  const { fields } = props;
  const [isMounted, setIsMounted] = useState(false);

  // Clear authentication state when logout page loads
  // Using direct sessionStorage/localStorage access instead of useAuth to avoid SSR issues
  useEffect(() => {
    setIsMounted(true);
    if (typeof window !== 'undefined') {
      // Clear the idle logout flag if it exists
      sessionStorage.removeItem('lma-idle-logout');
      sessionStorage.removeItem(CDP_PROFILE_DATA_STORAGE_KEY);
      // Clear auth token and username directly
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(USERNAME_STORAGE_KEY);
        window.localStorage.removeItem('lma-userId');
      }
    }
  }, []);

  const handleGoBack = () => {
    // Use Sitecore CTAButton href if available, otherwise empty
    const href = fields?.CTAButton?.value?.href;
    if (href) {
      window.location.href = href;
    }
  };

  // Prevent hydration mismatch by not rendering until mounted
  if (!isMounted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #255E5E 0%, #1a4a4a 100%)',
        padding: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 50 L45 65 L70 40' stroke='rgba(255,255,255,0.2)' stroke-width='4' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3Ccircle cx='50' cy='50' r='35' stroke='rgba(255,255,255,0.15)' stroke-width='3' fill='none'/%3E%3C/svg%3E"),
            url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M25 40 L35 50 L55 30' stroke='rgba(255,255,255,0.15)' stroke-width='3' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3Ccircle cx='40' cy='40' r='28' stroke='rgba(255,255,255,0.1)' stroke-width='2' fill='none'/%3E%3C/svg%3E"),
            radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.15) 0%, transparent 40%),
            radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.15) 0%, transparent 40%),
            radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
          `,
          backgroundSize: '100px 100px, 80px 80px, 200px 200px, 200px 200px, 300px 300px',
          backgroundPosition: '0 0, 50px 50px, 0 0, 100% 100%, 50% 50%',
          animation: 'successPatternMove 20s linear infinite',
          pointerEvents: 'none',
          zIndex: 0,
          opacity: 0.5,
        },
        '@keyframes successPatternMove': {
          '0%': {
            transform: 'translate(0, 0) rotate(0deg)',
          },
          '100%': {
            transform: 'translate(100px, 100px) rotate(360deg)',
          },
        },
      }}
    >
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={24}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 4,
            backgroundColor: 'white',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Success Icon */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                backgroundColor: '#e8f5e9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '4px solid #4caf50',
              }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: 60,
                  color: '#4caf50',
                }}
              />
            </Box>
          </Box>

          {/* Title - from Sitecore */}
          {fields?.Title?.value && (
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: '#212121',
                mb: 2,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                fontSize: {
                  xs: '1.75rem',
                  sm: '2rem',
                  md: '2.5rem',
                },
                lineHeight: 1.2,
              }}
            >
              <Text field={fields.Title} />
            </Typography>
          )}

          {/* Summary - from Sitecore */}
          {fields?.Summary?.value && (
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: '#616161',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              <Text field={fields.Summary} />
            </Typography>
          )}

          {/* Description (Instructions) - from Sitecore as RichText */}
          {fields?.Description?.value && (
            <Box
              sx={{
                backgroundColor: '#f1f8e9',
                borderRadius: 2,
                p: 3,
                mb: 4,
                textAlign: 'left',
                border: '1px solid #c8e6c9',
                '& h2': {
                  color: '#424242',
                  mb: 2,
                  fontWeight: 600,
                  fontSize: '1rem',
                  margin: 0,
                  marginBottom: '16px',
                },
                '& ul': {
                  pl: 3,
                  color: '#616161',
                  margin: 0,
                  '& li': {
                    mb: 1.5,
                    lineHeight: 1.8,
                  },
                },
              }}
            >
              <RichText field={fields.Description} />
            </Box>
          )}

          {/* CTA Button - from Sitecore */}
          {fields?.CTAButton?.value?.href && (
            <Button
              variant="contained"
              size="large"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              sx={{
                px: 4,
                py: 1,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                backgroundColor: '#255E5E',
                '&:hover': {
                  backgroundColor: '#1a4a4a',
                },
              }}
            >
              {fields.CTAButton.value.text}
            </Button>
          )}

          {/* Info Text - from Sitecore */}
          {fields?.InfoText?.value && (
            <Typography
              variant="body2"
              sx={{
                color: '#9e9e9e',
                mt: 4,
                fontStyle: 'italic',
              }}
            >
              <Text field={fields.InfoText} />
            </Typography>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Logout;
